import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Mail } from "lucide-react";

// ============================================================================
// GAZEAL ELITE Footer Reveal
// Scroll-based reveal with the Elite logo popping up, ported from Gazeal
// Advance. Gold neon glow instead of green.
// ============================================================================

const FOOTER_HEIGHT = 600;

export default function EliteFooterReveal() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const revealRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!revealRef.current) return;
      const rect = revealRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const visibleAmount = windowHeight - rect.top;
      const progress = Math.min(Math.max(visibleAmount / FOOTER_HEIGHT, 0), 1);
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (e, sectionId) => {
    e.preventDefault();
    const el = document.getElementById(sectionId);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div
      ref={revealRef}
      className="relative"
      style={{ height: `${FOOTER_HEIGHT}px` }}
      data-testid="elite-footer-reveal">

      {/* Fixed underneath footer */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-black"
        style={{
          height: `${FOOTER_HEIGHT}px`,
          zIndex: 1,
          pointerEvents: scrollProgress > 0.1 ? "auto" : "none"
        }}>

        {/* Neon gold glow strip at top */}
        <div
          className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#FFDE59] via-[#FF914D] to-[#FFDE59]"
          style={{
            boxShadow:
            "0 0 60px 20px rgba(255, 145, 77, 0.55), 0 0 100px 40px rgba(255, 222, 89, 0.25)",
            opacity: scrollProgress
          }} />


        {/* Large logo centered */}
        <div className="flex items-center justify-center h-3/5 pt-10">
          <img
            src="/gazeal-elite-logo.png"
            alt="Gazeal Elite"
            className="h-24 md:h-32 lg:h-40 w-auto"
            style={{
              opacity: scrollProgress,
              transform: `scale(${0.8 + scrollProgress * 0.2})`,
              transition: "transform 0.3s ease-out",
              filter: "drop-shadow(0 0 40px rgba(255,145,77,0.35))"
            }} />

        </div>

        {/* Footer content */}
        <div
          className="max-w-[1400px] mx-auto px-6 lg:px-12"
          style={{ opacity: scrollProgress }}>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Links */}
              <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8 font-manrope">
                <a
                  href="#secure"
                  onClick={(e) => handleNav(e, "secure")}
                  className="text-white/60 hover:text-[#FFDE59] transition-colors text-sm flex items-center gap-1">

                  Secure <ArrowUpRight className="w-3 h-3" />
                </a>
                <a
                  href="#advance"
                  onClick={(e) => handleNav(e, "advance")}
                  className="text-white/60 hover:text-[#FFDE59] transition-colors text-sm flex items-center gap-1">

                  Advance <ArrowUpRight className="w-3 h-3" />
                </a>
                <a
                  href="#faq"
                  onClick={(e) => handleNav(e, "faq")}
                  className="text-white/60 hover:text-[#FFDE59] transition-colors text-sm flex items-center gap-1">

                  FAQ <ArrowUpRight className="w-3 h-3" />
                </a>
                <a
                  href="https://gazeal.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-[#FFDE59] transition-colors text-sm flex items-center gap-1">

                  Gazeal Main Site <ArrowUpRight className="w-3 h-3" />
                </a>
              </div>

              {/* CTA */}
              <button
                type="button"
                onClick={(e) => handleNav(e, "access-section")}
                className="text-[14px] px-5 py-2.5 flex items-center gap-2 font-semibold rounded-full bg-gradient-to-r from-[#FFDE59] to-[#FF914D] text-black hover:opacity-90 transition-opacity font-manrope shadow-[0_4px_15px_rgba(255,145,77,0.3)]"
                data-testid="elite-footer-cta">

                Unlock Access
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>

            {/* Bottom bar */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-white/5 font-manrope">
              <p className="text-white/35 text-sm">
                © {new Date().getFullYear()} Gazeal Elite. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a
                  href="mailto:info@gazeal.co.uk"
                  className="text-white/35 hover:text-white/70 text-sm flex items-center gap-2 transition-colors">

                  <Mail className="w-4 h-4" /> info@gazeal.co.uk
                </a>
                <a href="#" className="text-white/35 hover:text-white/70 text-sm transition-colors">Privacy</a>
                <a href="#" className="text-white/35 hover:text-white/70 text-sm transition-colors">Terms</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide-up content overlay (reveals the footer as user scrolls) */}
      <div
        className="relative bg-black z-10"
        style={{
          transform: `translateY(-${scrollProgress * 100}%)`,
          transition: "transform 0.1s linear"
        }} />

    </div>);

}
